import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useParams } from 'react-router-dom';

import { Box, Grid, Hidden, Typography, useMediaQuery } from '@material-ui/core';
import { useTheme, withStyles } from '@material-ui/core/styles';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';

import { customer } from 'evee-ui-common/types';
import useLoadProfile from 'store/modules/profile/hooks/useLoadProfile';

import { Button, Divider, LinkButton } from 'evee-ui-common/components/base';
import useEditProfile from 'store/modules/profile/hooks/useEditProfile';

import AboutUser from 'widgets/profile/AboutUser';
import LinkTags from 'widgets/layout/LinkTags';
import MetaTags from 'widgets/layout/MetaTags';
import ProfileAvatar from 'widgets/profile/logic/ProfileAvatar';
import ProfileBasePageContainer from 'widgets/profile/ProfileBasePageContainer';
import ProfileRating from 'widgets/profile/ProfileRating';
import UserAvatar from 'widgets/profile/UserAvatar';
import UserInfo from 'widgets/profile/UserInfo';

import Listings from './Listings';
import ProfileReviews from './ProfileReviews';
import VerifiedDataList from './VerifiedDataList';
import styles from './styles';

const ProfilePage = ({
  account,
  isLoading,
  isAuth,
  currentUserId,
  reviews,
  vehicles,
  classes,
  onLoadCustomerProfile,
}) => {
  const { id } = useParams();
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { onSave, onFieldChanged } = useEditProfile();
  const { profile, onLoadProfile } = useLoadProfile();
  const { seo, firstName, address, licenceVerified, phoneVerified } = account;

  const isMe = account.id === currentUserId;
  const aboutText = isAuth && isMe ? profile.about : account.about;
  const [editMode, setEditMode] = useState(false);
  const [profileCopy, setProfileCopy] = useState(null);

  const handleEditProfile = () => {
    setProfileCopy(profile);
    setEditMode(true);
  };

  const handleCancelEditProfile = () => {
    onFieldChanged('about', profileCopy.about);
    setProfileCopy(null);
    setEditMode(false);
  };

  const handleSaveProfile = () => {
    setProfileCopy(null);
    setEditMode(false);
    onSave();
  };

  useEffect(() => {
    onLoadCustomerProfile(id);
  }, [id, onLoadCustomerProfile]);

  useEffect(() => {
    if (isAuth && isMe) {
      onLoadProfile();
    }
  }, [isAuth, isMe]);

  const editButtonJsx = isAuth && isMe && (
    <LinkButton
      disabled={editMode}
      text="Edit profile"
      style={{ alignSelf: 'flex-start' }}
      onClick={handleEditProfile}
    />
  );

  const profileActionsJsx = editMode ? (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <LinkButton text="Cancel" onClick={handleCancelEditProfile} />
      <Button text="Save" size="large" onClick={handleSaveProfile} />
    </Box>
  ) : (
    editButtonJsx
  );

  const shortAddress =
    address && (_.compact([address.suburb, address.state, address.country]).join(', ') || '');

  const locationJsx = (
    <Box display="flex" alignItems="center" marginBottom="24px">
      <HomeOutlinedIcon style={{ marginRight: '8px' }} />
      <Typography variant="body1">Lives in {shortAddress}</Typography>
    </Box>
  );

  const avatarJsx = (
    <Box className={classes.avatarContainer}>
      {isAuth && isMe ? (
        <ProfileAvatar
          progressSize={isTabletOrMobile ? 64 : 128}
          classes={{ profileAvatar: classes.avatar }}
          alignItems={isTabletOrMobile ? 'flex-end' : 'center'}
        />
      ) : (
        <UserAvatar
          loading={isLoading}
          account={account}
          justifyContent={isTabletOrMobile ? 'flex-end' : 'center'}
        />
      )}
    </Box>
  );

  return (
    <ProfileBasePageContainer bgColor="white" maxWidth={1100}>
      <>
        <LinkTags />

        {seo && (
          <MetaTags
            title={seo.title}
            keywords={seo.keywords}
            description={seo.description}
            ogImage={seo.photo}
          />
        )}
        <Grid container>
          <Hidden mdDown>
            <Grid item xs={12} lg={5} style={{ maxWidth: '372px' }}>
              <Box className={classes.profileSideContainer}>
                {avatarJsx}

                <ProfileRating account={account} loading={isLoading} />

                <Divider style={{ margin: theme.spacing(4, 0) }} />

                <VerifiedDataList
                  userName={firstName}
                  licenceVerified={licenceVerified}
                  phoneVerified={phoneVerified}
                />
              </Box>
            </Grid>
          </Hidden>

          <Grid item xs={12} lg={7}>
            <Box display="flex" flexDirection="column">
              {isTabletOrMobile ? (
                <Grid container spacing={2}>
                  <Grid item xs={7}>
                    <UserInfo account={account} loading={isLoading} />
                  </Grid>

                  <Grid item xs={5}>
                    {avatarJsx}
                  </Grid>

                  <Grid item xs={12}>
                    <Divider className={classes.divider} />
                  </Grid>

                  <Grid item xs={12}>
                    <AboutUser
                      value={aboutText}
                      edit={editMode}
                      loading={isLoading}
                      marginBottom="24px"
                    />

                    {locationJsx}

                    {profileActionsJsx}

                    <Divider className={classes.divider} />
                  </Grid>

                  <Grid item xs={12}>
                    <VerifiedDataList
                      userName={firstName}
                      licenceVerified={licenceVerified}
                      phoneVerified={phoneVerified}
                    />
                  </Grid>
                </Grid>
              ) : (
                <>
                  <UserInfo account={account} loading={isLoading} marginBottom="48px" />

                  <AboutUser
                    edit={editMode}
                    value={aboutText}
                    loading={isLoading}
                    marginBottom="24px"
                  />

                  {locationJsx}

                  {profileActionsJsx}
                </>
              )}

              <Divider className={classes.divider} />

              {vehicles.length > 0 && (
                <>
                  <Listings vehicles={vehicles} loading={isLoading} />
                  <Divider className={classes.divider} />
                </>
              )}

              <ProfileRating account={account} loading={isLoading} marginBottom="48px" />

              {(!!reviews.asOwner.length || !!reviews.asRenter.length) && (
                <Box id="reviews">
                  <ProfileReviews reviews={reviews} />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </>
    </ProfileBasePageContainer>
  );
};

ProfilePage.propTypes = {
  account: customer.customerPropType,
  currentUserId: PropTypes.string,
  isAuth: PropTypes.bool,
  isLoading: PropTypes.bool,
  reviews: customer.reviewsPropType,
  vehicles: PropTypes.array,
  classes: PropTypes.shape({
    avatar: PropTypes.string.isRequired,
    avatarContainer: PropTypes.string.isRequired,
    profileSideContainer: PropTypes.string.isRequired,
    divider: PropTypes.string.isRequired,
  }).isRequired,
  onLoadCustomerProfile: PropTypes.func,
};

ProfilePage.defaultProps = {
  account: {
    address: 'Sydney, NSW, Australia',
  },
  currentUserId: '',
  isAuth: false,
  isLoading: false,
  reviews: {
    asOwner: [],
    asRenter: [],
  },
  vehicles: [],
  onLoadCustomerProfile: () => {},
};

export default withStyles(styles)(ProfilePage);
